export const TASK_EVENTS = {
    LIST_VIEWED: 'task_list_viewed',
    SEARCHED: 'task_searched',
    FILTER_BY_STATUS: 'task_filter_by_status',
    OPENED: 'task_opened',
    DETAILS_VIEWED: 'task_details_viewed',
    DETAILS_TAB_VIEWED: 'task_details_tab_viewed',
    ARCHIVED: 'task_archived',
    RESOLVED: 'task_resolved',
    REOPENED: 'task_reopened',
    RESTORED: 'task_restored',
    NOTE_SENT: 'task_note_sent',
    CUSTOMER_VIEWED: 'task_customer_viewed',
    RECORDING_PLAYED: 'task_recording_played',
} as const;

export const INBOX_EVENTS = {
    SIDEBAR_FILTER_STATUS: 'inbox_sidebar_filter_status',
    SIDEBAR_FILTER_PRIORITY: 'inbox_sidebar_filter_priority',
    LIST_VIEWED: 'inbox_list_viewed',
    SEARCHED: 'inbox_searched',
    ITEM_CHECKED: 'inbox_item_checked',
    BULK_MARK_READ: 'inbox_bulk_mark_read',
    BULK_DELETED: 'inbox_bulk_deleted',
    ITEM_OPENED: 'inbox_item_opened',
    PAGINATION: 'inbox_pagination',
    ITEM_VIEWED: 'inbox_item_viewed',
    ITEM_TAB_VIEWED: 'inbox_item_tab_viewed',
    ITEM_SOLVED: 'inbox_item_solved',
    ITEM_REOPENED: 'inbox_item_reopened',
    ITEM_DELETED: 'inbox_item_deleted',
    ITEM_MARKED_NOT_SPAM: 'inbox_item_marked_not_spam',
    ITEM_NAVIGATED_BACK: 'inbox_item_navigated_back',
} as const;

export const OPPORTUNITY_EVENTS = {
    VIEW_SWITCHED: 'opp_view_switched',
    PIPELINE_SWITCHED: 'opp_pipeline_switched',
    SEARCHED: 'opp_searched',
    SORTED_BY: 'opp_sorted_by',
    FILTER_APPLIED: 'opp_filter_applied',
    FILTER_CLEARED: 'opp_filter_cleared',
    INSIGHTS_VIEWED: 'opp_insights_viewed',
    CREATED: 'opp_created',

    BOARD_COLUMN_TOGGLED: 'opp_board_column_toggled',
    BOARD_OPPORTUNITY_ADDED: 'opp_board_opportunity_added',
    BOARD_OPPORTUNITY_MOVED: 'opp_board_opportunity_moved',
    BOARD_CUSTOMER_VIEWED: 'opp_board_customer_viewed',
    BOARD_DETAILS_OPENED: 'opp_board_details_opened',
    BOARD_TODOS_VIEWED: 'opp_board_todos_viewed',
    BOARD_NOTES_VIEWED: 'opp_board_notes_viewed',
    BOARD_SMS_SENT: 'opp_board_sms_sent',
    BOARD_CONVERSATION_OPENED: 'opp_board_conversation_opened',
    BOARD_ACTION_MENU_USED: 'opp_board_action_menu_used',

    LIST_OPPORTUNITY_VIEWED: 'opp_list_opportunity_viewed',
    LIST_CUSTOMER_VIEWED: 'opp_list_customer_viewed',
    LIST_STAGE_CHANGED: 'opp_list_stage_changed',
    LIST_DETAILS_OPENED: 'opp_list_details_opened',
    LIST_EDITED: 'opp_list_edited',
    LIST_DELETED: 'opp_list_deleted',
    LIST_PAGINATION: 'opp_list_pagination',

    DETAILS_VIEWED: 'opp_details_viewed',
    DETAILS_TAB_VIEWED: 'opp_details_tab_viewed',
    DETAILS_STAGE_CHANGED: 'opp_details_stage_changed',
    DETAILS_EDITED: 'opp_details_edited',
    DETAILS_PIPELINE_SWITCHED: 'opp_details_pipeline_switched',
    DETAILS_DELETED: 'opp_details_deleted',
    DETAILS_CUSTOMER_VIEWED: 'opp_details_customer_viewed',
    DETAILS_CALL_CLICKED: 'opp_details_call_clicked',
    DETAILS_SMS_CLICKED: 'opp_details_sms_clicked',
    DETAILS_SUMMARY_VIEWED: 'opp_details_summary_viewed',
    DETAILS_RECORDING_PLAYED: 'opp_details_recording_played',
    DETAILS_NOTE_SENT: 'opp_details_note_sent',

    TODO_CREATED: 'opp_todo_created',
    TODO_COMPLETED: 'opp_todo_completed',
    TODO_EDITED: 'opp_todo_edited',
    TODO_CANCELLED: 'opp_todo_cancelled',

    TODOS_VIEW_VIEWED: 'opp_todos_view_viewed',
    TODOS_FILTER_APPLIED: 'opp_todos_filter_applied',
    TODOS_OVERDUE_VIEWED: 'opp_todos_overdue_viewed',
    TODOS_OPPORTUNITY_VIEWED: 'opp_todos_opportunity_viewed',
    TODOS_CUSTOMER_VIEWED: 'opp_todos_customer_viewed',
    TODOS_TODO_CREATED: 'opp_todos_todo_created',
    TODOS_TODO_COMPLETED: 'opp_todos_todo_completed',
    TODOS_TODO_EDITED: 'opp_todos_todo_edited',
    TODOS_TODO_CANCELLED: 'opp_todos_todo_cancelled',
} as const;

export const ALL_EVENTS = { ...TASK_EVENTS, ...INBOX_EVENTS, ...OPPORTUNITY_EVENTS } as const;
export type EventName = typeof ALL_EVENTS[keyof typeof ALL_EVENTS];

export interface TaskListViewedProperties {
    status: string;
}

export interface TaskSearchedProperties {
    query: string;
    resultsCount: number;
}

export interface TaskFilterByStatusProperties {
    status: 'open' | 'solved' | 'archived';
}

export interface TaskOpenedProperties {
    taskId: string;
    source: 'list';
}

export interface TaskDetailsViewedProperties {
    taskId: string;
}

export interface TaskDetailsTabViewedProperties {
    taskId: string;
    tab: 'highlights' | 'details' | 'activity' | 'notes';
}

export interface TaskArchivedProperties {
    taskId: string;
    source: 'details_drawer';
}

export interface TaskResolvedProperties {
    taskId: string;
    hasNote: boolean;
}

export interface TaskReopenedProperties {
    taskId: string;
}

export interface TaskRestoredProperties {
    taskId: string;
}

export interface TaskNoteSentProperties {
    taskId: string;
}

export interface TaskCustomerViewedProperties {
    taskId: string;
    customerId: string;
}

export interface TaskRecordingPlayedProperties {
    taskId: string;
    source: 'highlights' | 'details';
}

export type TaskEventProperties =
    | TaskListViewedProperties
    | TaskSearchedProperties
    | TaskFilterByStatusProperties
    | TaskOpenedProperties
    | TaskDetailsViewedProperties
    | TaskDetailsTabViewedProperties
    | TaskArchivedProperties
    | TaskResolvedProperties
    | TaskReopenedProperties
    | TaskRestoredProperties
    | TaskNoteSentProperties
    | TaskCustomerViewedProperties
    | TaskRecordingPlayedProperties
    | Record<string, unknown>;

export interface InboxSidebarFilterStatusProperties {
    status: 'inbox' | 'solved' | 'transferred' | 'spam' | 'trash';
}

export interface InboxSidebarFilterPriorityProperties {
    priority: 'urgent' | 'high' | 'medium' | 'low';
}

export interface InboxListViewedProperties {
    status: string;
    priority: string;
}

export interface InboxSearchedProperties {
    query: string;
    resultsCount: number;
}

export interface InboxItemCheckedProperties {
    itemId: string;
    isSelected: boolean;
}

export interface InboxBulkMarkReadProperties {
    itemCount: number;
}

export interface InboxBulkDeletedProperties {
    itemCount: number;
}

export interface InboxItemOpenedProperties {
    itemId: string;
}

export interface InboxPaginationProperties {
    direction: 'next' | 'prev';
    page: number;
}

export interface InboxItemViewedProperties {
    itemId: string;
}

export interface InboxItemTabViewedProperties {
    itemId: string;
    tab: 'overview' | 'activity';
}

export interface InboxItemSolvedProperties {
    itemId: string;
    hasNote: boolean;
}

export interface InboxItemReopenedProperties {
    itemId: string;
}

export interface InboxItemDeletedProperties {
    itemId: string;
}

export interface InboxItemMarkedNotSpamProperties {
    itemId: string;
}

export interface InboxItemNavigatedBackProperties {
    itemId: string;
}

export type InboxEventProperties =
    | InboxSidebarFilterStatusProperties
    | InboxSidebarFilterPriorityProperties
    | InboxListViewedProperties
    | InboxSearchedProperties
    | InboxItemCheckedProperties
    | InboxBulkMarkReadProperties
    | InboxBulkDeletedProperties
    | InboxItemOpenedProperties
    | InboxPaginationProperties
    | InboxItemViewedProperties
    | InboxItemTabViewedProperties
    | InboxItemSolvedProperties
    | InboxItemReopenedProperties
    | InboxItemDeletedProperties
    | InboxItemMarkedNotSpamProperties
    | InboxItemNavigatedBackProperties
    | Record<string, unknown>;

export interface OppViewSwitchedProperties {
    view: 'board' | 'list' | 'todos';
}

export interface OppPipelineSwitchedProperties {
    fromPipeline: string;
    toPipeline: string;
}

export interface OppSearchedProperties {
    query: string;
    resultsCount: number;
}

export interface OppSortedByProperties {
    sortBy: 'score' | 'createdAt' | 'updatedAt';
    direction: 'asc' | 'desc';
}

export interface OppFilterAppliedProperties {
    filters: Record<string, unknown>;
}

export interface OppInsightsViewedProperties {
    pipelineId: string;
}

export interface OppCreatedProperties {
    pipelineId: string;
    initialStage: string;
    source: 'header_button' | 'column_add';
}

export interface OppBoardColumnToggledProperties {
    column: string;
    action: 'collapse' | 'expand';
}

export interface OppBoardOpportunityAddedProperties {
    pipelineId: string;
    stage: string;
}

export interface OppBoardOpportunityMovedProperties {
    opportunityId: string;
    fromStage: string;
    toStage: string;
}

export interface OppBoardCustomerViewedProperties {
    opportunityId: string;
    customerId: string;
}

export interface OppBoardDetailsOpenedProperties {
    opportunityId: string;
    source: 'card_title';
}

export interface OppBoardTodosViewedProperties {
    opportunityId: string;
}

export interface OppBoardNotesViewedProperties {
    opportunityId: string;
}

export interface OppBoardSmsSentProperties {
    opportunityId: string;
    customerId: string;
}

export interface OppBoardConversationOpenedProperties {
    opportunityId: string;
    threadId: string;
}

export interface OppBoardActionMenuUsedProperties {
    opportunityId: string;
    action: 'call' | 'conversation';
}

export interface OppListOpportunityViewedProperties {
    opportunityId: string;
}

export interface OppListCustomerViewedProperties {
    opportunityId: string;
    customerId: string;
}

export interface OppListStageChangedProperties {
    opportunityId: string;
    fromStage: string;
    toStage: string;
}

export interface OppListDetailsOpenedProperties {
    opportunityId: string;
}

export interface OppListEditedProperties {
    opportunityId: string;
}

export interface OppListDeletedProperties {
    opportunityId: string;
}

export interface OppListPaginationProperties {
    direction: 'next' | 'prev';
    page: number;
}

export interface OppDetailsViewedProperties {
    opportunityId: string;
    initialTab?: string;
}

export interface OppDetailsTabViewedProperties {
    opportunityId: string;
    tab: 'details' | 'activity' | 'notes' | 'todos';
}

export interface OppDetailsStageChangedProperties {
    opportunityId: string;
    fromStage: string;
    toStage: string;
}

export interface OppDetailsEditedProperties {
    opportunityId: string;
}

export interface OppDetailsPipelineSwitchedProperties {
    opportunityId: string;
    fromPipeline: string;
    toPipeline: string;
}

export interface OppDetailsDeletedProperties {
    opportunityId: string;
}

export interface OppDetailsCustomerViewedProperties {
    opportunityId: string;
    customerId: string;
}

export interface OppDetailsCallClickedProperties {
    opportunityId: string;
    customerId: string;
}

export interface OppDetailsSmsClickedProperties {
    opportunityId: string;
    customerId: string;
}

export interface OppDetailsSummaryViewedProperties {
    opportunityId: string;
}

export interface OppDetailsRecordingPlayedProperties {
    opportunityId: string;
}

export interface OppDetailsNoteSentProperties {
    opportunityId: string;
}

export interface OppTodoCreatedProperties {
    opportunityId: string;
    pipelineId: string;
    stageId: string;
}

export interface OppTodoCompletedProperties {
    todoId: string;
    opportunityId: string;
}

export interface OppTodoEditedProperties {
    todoId: string;
    opportunityId: string;
}

export interface OppTodoCancelledProperties {
    todoId: string;
    opportunityId: string;
}

export interface OppTodosViewViewedProperties {
    pipelineId: string;
}

export interface OppTodosFilterAppliedProperties {
    filters: Record<string, unknown>;
}

export interface OppTodosOverdueViewedProperties {
    overdueCount: number;
}

export interface OppTodosOpportunityViewedProperties {
    opportunityId: string;
}

export interface OppTodosCustomerViewedProperties {
    opportunityId: string;
    customerId: string;
}

export type OpportunityEventProperties =
    | OppViewSwitchedProperties
    | OppPipelineSwitchedProperties
    | OppSearchedProperties
    | OppSortedByProperties
    | OppFilterAppliedProperties
    | OppInsightsViewedProperties
    | OppCreatedProperties
    | OppBoardColumnToggledProperties
    | OppBoardOpportunityAddedProperties
    | OppBoardOpportunityMovedProperties
    | OppBoardCustomerViewedProperties
    | OppBoardDetailsOpenedProperties
    | OppBoardTodosViewedProperties
    | OppBoardNotesViewedProperties
    | OppBoardSmsSentProperties
    | OppBoardConversationOpenedProperties
    | OppBoardActionMenuUsedProperties
    | OppListOpportunityViewedProperties
    | OppListCustomerViewedProperties
    | OppListStageChangedProperties
    | OppListDetailsOpenedProperties
    | OppListEditedProperties
    | OppListDeletedProperties
    | OppListPaginationProperties
    | OppDetailsViewedProperties
    | OppDetailsTabViewedProperties
    | OppDetailsStageChangedProperties
    | OppDetailsEditedProperties
    | OppDetailsPipelineSwitchedProperties
    | OppDetailsDeletedProperties
    | OppDetailsCustomerViewedProperties
    | OppDetailsCallClickedProperties
    | OppDetailsSmsClickedProperties
    | OppDetailsSummaryViewedProperties
    | OppDetailsRecordingPlayedProperties
    | OppDetailsNoteSentProperties
    | OppTodoCreatedProperties
    | OppTodoCompletedProperties
    | OppTodoEditedProperties
    | OppTodoCancelledProperties
    | OppTodosViewViewedProperties
    | OppTodosFilterAppliedProperties
    | OppTodosOverdueViewedProperties
    | OppTodosOpportunityViewedProperties
    | OppTodosCustomerViewedProperties
    | Record<string, unknown>;

export type EventProperties =
    TaskEventProperties |
    InboxEventProperties |
    OpportunityEventProperties;
